import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, Btn } from "@/components/primitives";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Info, Navigation, UserPlus, CheckCircle2 } from "lucide-react";

const inputCls = "w-full h-10 px-3 rounded-md border border-curtron-line bg-curtron-surface text-sm outline-none focus:border-curtron-green focus:ring-2 focus:ring-curtron-green/20 transition";
const labelCls = "text-[11px] uppercase tracking-[0.05em] font-medium text-curtron-sub";

function Field({ label, required, value, onChange, ph, type = "text", testid }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-curtron-terracotta"> *</span>}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={ph}
        data-testid={testid} className={`${inputCls} mt-1`} />
    </div>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const FLAGS = [["house_call", "House Call"], ["assist", "Assist"], ["ice", "ICE"], ["body_bag", "Body Bag"]];

const EMPTY = {
  deceased_name: "", agency_for: "", pickup_location: "", dropoff_location: "",
  pickup_facility_name: "", dropoff_facility_name: "",
  pickup_city_state: "", dropoff_city_state: "", scheduled_at: "",
  assigned_to: "", assigned_to_name: "", assigned_to_secondary: "", assigned_to_secondary_name: "",
  driver_name: "", van_number: "", mileage: "", morgue_date_in: "", morgue_date_out: "",
  lifelink_donor: "", gdc_prisoner: "", personal_belongings: "", notes: "",
  house_call: false, assist: false, ice: false, body_bag: false, status: "dispatched",
};

export default function TransportForm() {
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(EMPTY);
  const [agencies, setAgencies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customAgency, setCustomAgency] = useState(false);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const assignToMe = () => setForm((f) => ({
    ...f, assigned_to: user.id, assigned_to_name: user.name,
    driver_name: user.name, van_number: user.van_number || f.van_number,
  }));
  const clearMe = () => setForm((f) => ({ ...f, assigned_to: "", assigned_to_name: "", driver_name: "" }));
  const iAmAssigned = user && form.assigned_to === user.id;

  useEffect(() => {
    api.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {});
    api.get("/users").then((r) => setDrivers(r.data.filter((u) => u.role === "driver" || u.role === "admin"))).catch(() => {});
    if (editing) {
      api.get(`/calls/${id}`).then((r) => {
        setForm({ ...EMPTY, ...r.data, scheduled_at: toLocalInput(r.data.scheduled_at) });
        setLoading(false);
      }).catch(() => { toast.error("Failed to load transport"); setLoading(false); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onAgencyPick = (name) => {
    const a = agencies.find((x) => x.name === name);
    setForm((f) => ({
      ...f,
      agency_for: name,
      dropoff_facility_name: a ? a.name : f.dropoff_facility_name,
      dropoff_location: a?.address || f.dropoff_location,
      dropoff_city_state: a ? [a.city, a.state, a.zip].filter(Boolean).join(", ") : f.dropoff_city_state,
    }));
  };

  const onDriver = (uid) => {
    const d = drivers.find((x) => x.id === uid);
    setForm((f) => ({ ...f, assigned_to: uid, assigned_to_name: d?.name || "", driver_name: d?.name || f.driver_name, van_number: d?.van_number || f.van_number }));
  };
  const onBackup = (uid) => {
    const d = drivers.find((x) => x.id === uid);
    setForm((f) => ({ ...f, assigned_to_secondary: uid, assigned_to_secondary_name: d?.name || "" }));
  };

  const pickNearest = async () => {
    try {
      const { data } = await api.get("/fleet");
      const live = (data.drivers || []).filter((d) => d.lat != null);
      if (live.length === 0) { toast.info("No drivers are broadcasting GPS right now."); return; }
      const d = live[0];
      const match = drivers.find((x) => x.id === d.id) || d;
      onDriver(match.id);
      toast.success(`Assigned nearest live driver: ${match.name || "driver"}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.deceased_name.trim() || !form.agency_for.trim() || !form.pickup_location.trim() || !form.dropoff_location.trim()) {
      toast.error("Agency, deceased name, pickup and drop-off are required");
      return;
    }
    setSaving(true);
    const payload = { ...form };
    payload.scheduled_at = payload.scheduled_at ? new Date(payload.scheduled_at).toISOString() : new Date().toISOString();
    if (!payload.assigned_to) { payload.assigned_to = null; payload.assigned_to_name = null; }
    if (!payload.assigned_to_secondary) { payload.assigned_to_secondary = null; payload.assigned_to_secondary_name = null; }
    try {
      if (editing) {
        await api.patch(`/calls/${id}`, payload);
        toast.success("Transport updated");
        navigate(`/transports/${id}`);
      } else {
        const { data } = await api.post("/calls", payload);
        toast.success("Call dispatched");
        navigate(`/transports/${data.id || ""}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="max-w-3xl">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-curtron-sub hover:text-curtron-ink mb-3 transition-colors" data-testid="form-back">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-curtron-ink tracking-tight mb-4">
        {editing ? "Edit Transport Call" : "New Transport Call"}
      </h1>

      <form onSubmit={submit} className="space-y-4">
        {!editing && (
          <div className="flex gap-3 rounded-md border border-curtron-info/30 bg-curtron-info/8 px-4 py-3" data-testid="new-call-info">
            <Info className="w-5 h-5 text-curtron-info shrink-0 mt-0.5" />
            <p className="text-sm text-curtron-info">
              Enter what you know now. The driver will finalize the rest (ICE, body bag, signature, paperwork) and the Run Sheet PDF will generate when they mark the call <span className="font-medium">Completed</span>.
            </p>
          </div>
        )}

        <Card className="p-4 sm:p-5 space-y-4">
          <div>
            <label className={labelCls}>Agency For <span className="text-curtron-terracotta">*</span></label>
            {!customAgency ? (
              <select value={form.agency_for} onChange={(e) => onAgencyPick(e.target.value)} data-testid="field-agency_for" className={`${inputCls} mt-1`}>
                <option value="">Tap to choose funeral home</option>
                {agencies.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
              </select>
            ) : (
              <input value={form.agency_for} onChange={(e) => set("agency_for", e.target.value)} placeholder="Custom agency name"
                data-testid="field-agency_custom" className={`${inputCls} mt-1`} />
            )}
            <button type="button" onClick={() => setCustomAgency((c) => !c)} className="text-xs text-curtron-green mt-1.5" data-testid="toggle-custom-agency">
              {customAgency ? "…or choose from the directory" : "…or type a custom agency name"}
            </button>
          </div>

          <Field label="Deceased Name" required value={form.deceased_name} onChange={(v) => set("deceased_name", v)} ph="Full name" testid="field-deceased_name" />
          <Field label="Pickup Location" required value={form.pickup_location} onChange={(v) => set("pickup_location", v)} ph="Address or facility" testid="field-pickup_location" />
          <Field label="Pickup Facility Name" value={form.pickup_facility_name} onChange={(v) => set("pickup_facility_name", v)} ph="e.g. Memorial Health, County Coroner" testid="field-pickup_facility_name" />
          <Field label="Pickup City, State" value={form.pickup_city_state} onChange={(v) => set("pickup_city_state", v)} ph="e.g. Savannah, GA" testid="field-pickup_city_state" />
          <Field label="Drop-off Location" required value={form.dropoff_location} onChange={(v) => set("dropoff_location", v)} ph="Address or facility (may change)" testid="field-dropoff_location" />
          <Field label="Drop-off Facility Name" value={form.dropoff_facility_name} onChange={(v) => set("dropoff_facility_name", v)} ph="e.g. Fox & Weeks Funeral Home" testid="field-dropoff_facility_name" />
          <Field label="Drop-off City, State" value={form.dropoff_city_state} onChange={(v) => set("dropoff_city_state", v)} ph="e.g. Portal, GA" testid="field-dropoff_city_state" />
          <div>
            <label className={labelCls}>Scheduled Date/Time</label>
            <input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="field-scheduled_at" className={`${inputCls} mt-1`} />
          </div>
        </Card>

        <Card className="p-4 sm:p-5 space-y-4">
          <div>
            <label className={labelCls}>Assign Primary Driver</label>
            <div className="flex gap-2 mt-1">
              <select value={form.assigned_to || ""} onChange={(e) => onDriver(e.target.value)} data-testid="field-assigned_to" className={`${inputCls} flex-1`}>
                <option value="">Tap to assign</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.role === "admin" ? " · ADMIN" : ""}{d.van_number ? ` · ${d.van_number}` : ""}</option>)}
              </select>
              <Btn type="button" variant="secondary" onClick={pickNearest} data-testid="nearest-driver-btn"><Navigation className="w-4 h-4" /> Nearest</Btn>
            </div>
            {isAdmin && (
              iAmAssigned ? (
                <div className="flex items-center gap-2 mt-2 text-sm text-curtron-green" data-testid="assigned-to-me-badge">
                  <CheckCircle2 className="w-4 h-4" /> You are assigned as the primary driver
                  <button type="button" onClick={clearMe} className="text-xs text-curtron-sub underline ml-1" data-testid="clear-assign-me">Clear</button>
                </div>
              ) : (
                <button type="button" onClick={assignToMe} data-testid="assign-to-me-btn"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-curtron-green border border-dashed border-curtron-green/50 rounded-full px-3 py-1.5 hover:bg-curtron-green/5 transition-colors">
                  <UserPlus className="w-4 h-4" /> Assign to me ({user?.name})
                </button>
              )
            )}
          </div>

          <div>
            <label className={labelCls}>Assign Back-up Driver (optional)</label>
            <select value={form.assigned_to_secondary || ""} onChange={(e) => onBackup(e.target.value)} data-testid="field-assigned_to_secondary" className={`${inputCls} mt-1`}>
              <option value="">None — leave unassigned</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.role === "admin" ? " · ADMIN" : ""}</option>)}
            </select>
            <p className="text-[11px] text-curtron-muted mt-1.5">
              A back-up assists with loading &amp; scene. They see the call, accept, mark on-scene, and mark completed — but do not fill agency paperwork.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Driver" value={form.driver_name} onChange={(v) => set("driver_name", v)} ph="Driver name" testid="field-driver_name" />
            <Field label="Van #" value={form.van_number} onChange={(v) => set("van_number", v)} ph="e.g. V-12" testid="field-van_number" />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="field-notes" rows={3} placeholder="Additional notes…" className={`${inputCls} mt-1 h-auto py-2`} />
          </div>
        </Card>

        {editing && (
          <Card className="p-4 sm:p-5">
            <h2 className="font-heading font-semibold text-curtron-ink mb-3">Driver Delivery Sheet</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Mileage" value={form.mileage} onChange={(v) => set("mileage", v)} testid="field-mileage" />
              <Field label="Personal Belongings" value={form.personal_belongings} onChange={(v) => set("personal_belongings", v)} testid="field-personal_belongings" />
              <Field label="Morgue Date In" ph="MM/DD/YYYY" value={form.morgue_date_in} onChange={(v) => set("morgue_date_in", v)} testid="field-morgue_date_in" />
              <Field label="Morgue Date Out" ph="MM/DD/YYYY" value={form.morgue_date_out} onChange={(v) => set("morgue_date_out", v)} testid="field-morgue_date_out" />
              <Field label="LifeLink Donor" value={form.lifelink_donor} onChange={(v) => set("lifelink_donor", v)} testid="field-lifelink_donor" />
              <Field label="GDC Prisoner" value={form.gdc_prisoner} onChange={(v) => set("gdc_prisoner", v)} testid="field-gdc_prisoner" />
            </div>
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-curtron-line">
              {FLAGS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-curtron-ink cursor-pointer">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`flag-${k}`} className="w-4 h-4 accent-[#2C4C3B]" />
                  {l}
                </label>
              ))}
            </div>
          </Card>
        )}

        <div className="flex justify-end gap-2 pb-4">
          <Btn type="button" variant="secondary" onClick={() => navigate(-1)}>Cancel</Btn>
          <Btn type="submit" disabled={saving} data-testid="save-transport-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Save & regenerate PDF" : "Dispatch Call"}
          </Btn>
        </div>
      </form>
    </div>
  );
}
